import { Spin, message } from "antd";
import React from "react";
import BaseForm from "../../components/Form/BaseForm";
import {
  APIError,
  FormFieldConfig,
  Opportunity,
  User,
} from "../../helpers/shared-interfaces";

import * as Sentry from "@sentry/browser";
import { FormInstance } from "antd/lib/form";
import { AxiosResponse } from "axios";
import debounce from "lodash.debounce";
import StepContainer from "../../components/StepContainer";
import {
  generateDisplayErrorMessage,
  handleValidationErrors,
  mapAPIErrorToFormError,
  request,
  responseHandler,
} from "../../helpers/utilities";
import { PageNames } from "../NewOpportunity";
import { ASSIGN_REFERRER_TO_OPPORTUNITY } from "../../gql/opportunity";
import { ALL_USERS_QUERY } from "../../gql/users";
import { DEBOUNCE_TIME } from "../../helpers/constants";


interface Props {
  opportunityReferrer?: string;
  opportunity: Opportunity;
  getNextViewName: () => string | null;
  getPreviousViewName: () => string | null;
  setPercentage: (percentage: number) => void;
  next: () => void;
  prev: () => void;
}

interface State {
  users: User[];
  isLoading: boolean;
  userSearchTerm: string;
  isSearching: boolean;
}

export default class OpportunityReferral extends React.Component<Props, State> {
  reference = React.createRef<FormInstance>();

  constructor(props: Readonly<Props>) {
    super(props);
    this.state = {
      isLoading: true,
      users: [],
      userSearchTerm: "",
      isSearching: false,
    };
    this.getAllUsers = this.getAllUsers.bind(this);
    this.assignReferrerToOpportunity =
      this.assignReferrerToOpportunity.bind(this);
  }

  setRef = (ref: React.RefObject<FormInstance>): void => {
    this.reference = ref;
  };

  getAllUsers(): void {
    const { userSearchTerm } = this.state;
    this.setState({ isSearching: true });

    request({
      query: ALL_USERS_QUERY,
      variables: {
        page: 1,
        q: userSearchTerm,
      },
    }).then(({ data: { data } }: any): void => {
      this.setState({
        users: data.users.results,
        isLoading: false,
        isSearching: false,
      });
    });
  }

  triggerUserDelayedSearch = debounce(async (): Promise<void> => {
    this.getAllUsers();
  }, DEBOUNCE_TIME);

  handleUserSearchInput = async (userSearchTerm: string): Promise<void> => {
    this.setState({ userSearchTerm }, () => {
      this.triggerUserDelayedSearch();
    });
  };

  componentDidMount(): void {
    this.props.setPercentage(10);
    interface Response {
      data: {
        users: User[];
      };
    }
    this.getAllUsers();
  }

  assignReferrerToOpportunity<T>(
    referrerId: string,
  ): Promise<AxiosResponse<T>> {
    const { opportunity } = this.props;
    return request({
      query: ASSIGN_REFERRER_TO_OPPORTUNITY,
      variables: {
        opportunityId: opportunity.id,
        referrerId: referrerId,
      },
    });
  }

  generateNextViewName = (): string | null => {
    return PageNames.opportunityProspectPage;
  };

  next = async (): Promise<void> => {
    interface Response {
      data: {
        assignReferrerToOpportunity: {
          referrer: User;
          errors: APIError[];
        };
      };
    }

    if (!this.reference.current) return;

    let data;

    try {
      data = await this.reference.current.validateFields(["referrer"]);
    } catch (error) {
      handleValidationErrors(error);
      return;
    }

    this.setState({ isLoading: true });

    const assignMutation = this.assignReferrerToOpportunity;

    try {
      const response: AxiosResponse<Response> = await assignMutation<Response>(
        data.referrer,
      );

      const { err, handled } = responseHandler<Response>(
        response,
        "assignReferrerToOpportunity",
      );

      this.setState({ isLoading: false });

      if (handled) {
        message.error("An Error Occurred. Your request couldn't be processed.");
        return;
      } else if (err) {
        message.error(generateDisplayErrorMessage(err));
        this.reference.current.setFields(err.map(mapAPIErrorToFormError));
      } else {
        this.props.next();
      }
    } catch (error) {
      this.setState({ isLoading: false });

      message.error(`Request Failed - ${generateDisplayErrorMessage(error)}`);
      Sentry.captureException(error);
    }
  };

  render(): JSX.Element {
    const { isLoading, users, isSearching } = this.state;
    const { opportunityReferrer } = this.props;

    const fields: FormFieldConfig[] = [
      {
        id: "referrer",
        name: "referrer",
        inputType: "search",
        rule: "string",
        placeholder: "Referrer",
        label: "Referrer",
        required: true,
        isSearching,
        colConfig: {
          span: 11,
        },
        initialValue: opportunityReferrer,
        selectOptions: users,
        changeHandler: this.handleUserSearchInput,
      },
    ];

    return (
      <Spin spinning={isLoading}>
        <StepContainer
          getNextViewName={this.generateNextViewName}
          getPreviousViewName={this.props.getPreviousViewName}
          style={{
            position: "relative",
            top: "-10px",
          }}
          config={{
            prev: this.props.prev,
            next: this.next,
            showNext: true,
            showPrev: true,
          }}
        >
          <BaseForm
            reference={this.reference}
            layout="vertical"
            fieldsToHide={[]}
            fields={fields}
          ></BaseForm>
        </StepContainer>
      </Spin>
    );
  }
}
